// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — loopback WS client core (ABB-S3)
// ═══════════════════════════════════════════════════════════════════════════
//
// The in-page loopback WS client that pairs with the already-shipped node-
// companion broker. THIS SLICE (S3) is a NON-FUNCTIONAL-END-TO-END FOUNDATION:
// it connects + handshakes + heartbeats + routes inbound frames, but EXECUTES
// NOTHING — every rpc handler is injected by a LATER slice via DI (S4 command.run,
// S6 state.read/export.dxf, MANIFEST manifest.read). A green S3 is NOT a working
// bridge (spec line 95, P2).
//
// WHAT S3 OWNS (spec lines 94-118 / SM-11..14):
//   • WS lifecycle connect(token) / disconnect, HARDCODED loopback URL.
//   • `hello` frame with the token IN THE FRAME (never the URL/query).
//   • `welcome` parse → cache sessionEpoch + quad + role + protocolVersion;
//     a protocolVersion MISMATCH refuses the connected state (fail-closed).
//   • Accept `welcome` AND `session-changed` at ANY time post-pairing → adopt
//     the new epoch/quad (idempotent for the auto-promotion welcome+changed pair).
//   • Inbound rpc dispatch by method to DI handlers; a known-but-unwired OR
//     unknown method replies `rpc-error{browser-dispatch-error}` (never a silent
//     drop — that hangs the companion deadline). An rpc whose sessionEpoch ≠
//     current welcome epoch is epoch-DROPped.
//   • Heartbeat: receive ping → send pong synchronously from page JS.
//   • Reconnect: on drop ABORT the in-progress turn (no result delivery) +
//     clear staged buffer + backoff + fresh hello → new welcome → new epoch.
//     On 1008 (token rotation) NO auto-reconnect → re-prompt status.
//   • Role / demotion tracking: derive role from welcome + each session-changed.
//   • DEFAULT-OFF: attaches NOTHING on page load (INV-OFF-1); connect only on an
//     explicit connect() call.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): this module imports ONLY `ai-commands` (the protocol
// version + reject codes, via the codec/leaf modules) — NEVER editorStore (not
// even a type-only `import().Type`). ALL editor coupling (dispatch / getState /
// handlers / revision getters) arrives via the `AgentBridgeClientDeps` DI object,
// typed against plain structural types declared in `./clientTypes` (split out for
// the 500-line rule). `npm run deps:graph` must report cycles=0 after this slice
// (it ESTABLISHES the boundary).
// ═══════════════════════════════════════════════════════════════════════════

import { HeartbeatResponder } from "./heartbeat";
import {
  assertLoopbackWsTarget,
} from "./loopbackGuard";
import {
  type AgentBridgeClientDeps,
  type AgentBridgeStatus,
  type BridgeSocketFactory,
  type BridgeSocketLike,
  defaultSocketFactory,
  type RpcHandlerOutcome,
} from "./clientTypes";
import {
  ReconnectScheduler,
  shouldAutoReconnect,
  WS_CLOSE_NORMAL,
  WS_CLOSE_PROTOCOL_MISMATCH,
  WS_CLOSE_TOKEN_ROTATED,
} from "./reconnect";
import { SessionTracker } from "./sessionTracker";
import {
  AGENT_BRIDGE_PROTOCOL_VERSION,
  type BridgeRole,
  buildDocumentChangedFrame,
  buildHelloFrame,
  buildPushFrame,
  type InboundExportControlFrame,
  type InboundRpcFrame,
  type InboundSessionChangedFrame,
  type InboundWelcomeFrame,
  buildExportChunkFrame,
  isKnownRpcMethod,
  type OutboundDocumentChangedFrame,
  type OutboundExportChunkFrame,
  type OutboundPushFrame,
  parseInboundFrame,
  serializeFrame,
  type SessionQuad,
} from "./wsFrameCodec";
import { BRIDGE_AUDIT_ACTOR_EMAIL } from "./auditStaging";
import {
  type FailureOutcome,
  serializeFailureTerminal,
  serializeFallbackTerminal,
  serializeSuccessTerminal,
  type SuccessOutcome,
} from "./outboundTerminals";

/** The HARDCODED loopback connect target (§3 / SM-11; loopback-only, port 8765). */
export const AGENT_BRIDGE_WS_URL = "ws://127.0.0.1:8765/agent-bridge/ws";

/**
 * The loopback WS client. DEFAULT-OFF: constructing it attaches NOTHING and
 * opens no socket (INV-OFF-1) — `connect(token)` is the only thing that opens a
 * socket. Owns the socket lifecycle, the cached ownership quad/role/epoch, the
 * heartbeat responder, the reconnect scheduler, and the inbound-frame dispatch.
 */
export class AgentBridgeClient {
  private readonly deps: AgentBridgeClientDeps;

  private readonly socketFactory: BridgeSocketFactory;

  private readonly wsUrl: string;

  private readonly reconnectScheduler: ReconnectScheduler;

  private socket: BridgeSocketLike | null = null;

  private heartbeat: HeartbeatResponder | null = null;

  /** The token for the CURRENT connect attempt (volatile; cleared on disconnect). */
  private token: string | null = null;

  /** The authoritative ownership quad/role/epoch tracker (SM-12 / SM-15 demotion). */
  private readonly session: SessionTracker;

  private status: AgentBridgeStatus = "disconnected";

  /** Whether the socket reached `welcome` (vs onerror/onclose-before-open). */
  private welcomed = false;

  /** Whether the current close is a deliberate disconnect() (suppresses reconnect). */
  private deliberateClose = false;

  /**
   * A monotonic client-side CONNECTION GENERATION counter, bumped on every new
   * `openSocket()`. Captured (alongside the socket REFERENCE + the welcome `bootId`)
   * when an async rpc handler is DISPATCHED so a late result can be bound to the
   * EXACT connection it was dispatched on — a reconnect to a RESTARTED companion can
   * REUSE the same `sessionEpoch` under a NEW `bootId`, so the numeric-epoch guard
   * alone could settle the WRONG command on the new connection (P1).
   */
  private connectionGeneration = 0;

  /**
   * [round-4 P2 #4] The set of IN-FLIGHT exports keyed by `correlationId` → the export's
   * ORIGIN sessionEpoch (the epoch the `export.dxf` rpc was dispatched at). The companion
   * stamps EVERY export FLOW-CONTROL frame (`export-pause`/`-resume`/`-cancel`) with the
   * export's ORIGIN epoch — `exportBroker.ts` `PendingExport.sessionEpoch`, captured at
   * `beginExport` from `host.currentEpoch()` (lines 184/269/291-293/345), NOT the current
   * welcome epoch. During a document swap / soft re-key WHILE an export produces, the
   * companion ABORTS the pending export + sends `export-cancel{sessionEpoch:ORIGIN}` to the
   * ORIGINATING socket — but by then the client's current welcome epoch has ADVANCED past
   * the origin (the soft-rekey re-`welcome`d at epoch+1). The old current-epoch-equality
   * gate would DROP that cancel → the wired S6 producer keeps streaming useless old-epoch
   * chunks after the HTTP response was already aborted. So an export-control frame is routed
   * when its `(correlationId, sessionEpoch)` matches an in-flight export's ORIGIN — even
   * after the socket soft-rekeys. An entry is added when an `export.dxf` rpc with a wired
   * handler passes the epoch-drop gate, and removed when its terminal is sent OR the
   * connection resets (a drop makes any pending export's controls undeliverable anyway).
   */
  private readonly inFlightExportOriginEpochs = new Map<string, number>();

  constructor(deps: AgentBridgeClientDeps) {
    this.deps = deps;
    this.socketFactory = deps.socketFactory ?? defaultSocketFactory;
    this.wsUrl = deps.wsUrl ?? AGENT_BRIDGE_WS_URL;
    this.reconnectScheduler = new ReconnectScheduler(deps.reconnect);
    this.session = new SessionTracker(deps.onRoleChange);
  }

  // ── public surface ─────────────────────────────────────────────────────────

  /** The current connection status (a typed enum; S7's UI maps it to i18n strings). */
  getStatus(): AgentBridgeStatus {
    return this.status;
  }

  /** The current seat role, or null if not paired (active vs observer). */
  getRole(): BridgeRole | null {
    return this.session.getRole();
  }

  /** The current ownership epoch the browser stamps on sent frames, or null. */
  getCurrentEpoch(): number | null {
    return this.session.getCurrentEpoch();
  }

  /** The cached authoritative ownership quad from the latest welcome, or null. */
  getSessionQuad(): SessionQuad | null {
    return this.session.getQuad();
  }

  /**
   * Whether I am the CURRENT ACTIVE epoch (S4/S5 gate execution + pushes on this).
   * True only when paired active AND no later broadcast epoch has superseded my
   * welcomed epoch (a `session-changed` with a higher epoch = I am demoted).
   */
  isCurrentActiveEpoch(): boolean {
    return this.session.isCurrentActiveEpoch();
  }

  /**
   * Open the loopback WS and pair (the ONLY thing that opens a socket — INV-OFF-1).
   * The token rides the HELLO FRAME (sent on `onopen`), NEVER the URL/query. The
   * connect URL is asserted loopback before the socket opens (fail-closed).
   */
  connect(token: string): void {
    if (this.socket !== null) {
      return; // already connected / connecting (single socket).
    }
    // [round-2 finding E] VALIDATE the token BEFORE opening a socket: an empty/blank
    // token would ride a hello the companion rejects 1008 `bad-hello` (an empty token
    // is not a valid hello shape) / `unauthorized` — and the SAME blank token would
    // re-fail forever. Fail-closed UP FRONT (open no socket) + surface the re-prompt
    // status so S7's UI prompts for a token rather than a doomed connect loop.
    const trimmedToken = token.trim();
    if (trimmedToken.length === 0) {
      this.token = null;
      this.setStatus("token-rejected");
      return;
    }
    // [P2 double-open] During backoff `this.socket` is null while a reconnect timer
    // is ARMED — a re-entrant connect() here would open socket #2, then the armed
    // timer fires and opens socket #3, orphaning #2's listeners. Cancel + reset the
    // scheduler so the pending reconnect cannot also fire (cancel clears the timer;
    // reset zeroes the backoff so this fresh connect is "connecting", not
    // "reconnecting" at an elevated delay — P3).
    this.reconnectScheduler.cancel();
    this.reconnectScheduler.reset();
    assertLoopbackWsTarget(this.wsUrl); // refuse a non-loopback target up front.
    // [round-3 P3 #7] Store + later SEND the TRIMMED token: the companion compares the
    // hello token EXACTLY, so a pasted token with trailing whitespace/newline (the
    // visible token is correct) would be rejected `unauthorized` + re-prompted forever.
    // Trim once here so the hello (and any reconnect re-hello off this.token) carries the
    // clean value.
    this.token = trimmedToken;
    this.deliberateClose = false;
    this.openSocket();
  }

  /** Deliberately tear down the socket (suppresses reconnect; zeroes the token). */
  disconnect(): void {
    this.deliberateClose = true;
    this.reconnectScheduler.cancel();
    // [P3 backoff-leak] Also RESET the scheduler (cancel only clears the armed
    // timer; the attempt/delay counters would otherwise leak so a later fresh
    // connect() mislabels its first attempt 'reconnecting' at an elevated backoff).
    this.reconnectScheduler.reset();
    this.token = null; // zero the volatile token on disconnect (INV-LOOPBACK).
    if (this.socket !== null) {
      const s = this.socket;
      this.socket = null;
      this.detachHandlers(s);
      try {
        // [round-4 P2 #1] WS_CLOSE_NORMAL (1000) is browser-allowed; a clean teardown.
        s.close(WS_CLOSE_NORMAL, "client-disconnect");
      } catch {
        /* close on an already-closed socket is harmless */
      }
    }
    this.heartbeat = null;
    // [P2 disconnect-abort] A deliberate disconnect MID-TURN must abort the
    // in-progress turn — the drop⇔disconnect SYMMETRY invariant. onClose fires the
    // abort on a DROP, but disconnect() detaches the handlers BEFORE close() so
    // onClose never runs; without this the turn never aborts → S4's in-flight latch
    // + S5's staged audit leak into the next session. Single-fire holds BY
    // CONSTRUCTION: the handlers are already detached above, so onClose cannot also
    // run for this teardown — exactly one abort per disconnect.
    this.deps.onAbortInProgressTurn?.();
    this.session.reset();
    // [round-4 P2 #4] Symmetry with onClose: a deliberate disconnect also ends every
    // in-flight export → clear the origin-epoch registry (no leak into the next session).
    this.inFlightExportOriginEpochs.clear();
    // ABB-S6.4 (1A) HYDRATE-IN reset: a deliberate disconnect clears the document belief so a
    // later read never binds to a stale pre-disconnect document.
    this.notifyDocumentBeliefReset();
    this.setStatus("disconnected");
  }

  /**
   * Send a control `document-changed` frame on a true document SWAP (S5 calls
   * this). Stamps the NEW documentEpoch; only sent while a socket is open.
   *
   * [P2 pre-welcome] DROPPED before `welcome`: a document swap reported after WS
   * `open` but before `welcome` would arrive as a pre-pairing frame the companion's
   * unpaired path only accepts a `hello` for — it closes 1008 `bad-hello`, which
   * this client would mislabel a token reject. The gate lives HERE (not in S5) so a
   * later caller can never send a non-hello app frame pre-welcome. A swap during the
   * pre-welcome window is INERT: the fresh `hello` already carries the live document
   * identity (`getHelloDocument` reads the current document at open), and any post-
   * welcome swap re-sends correctly.
   */
  sendDocumentChanged(documentId: string | null, documentEpoch: number | null): void {
    if (!this.welcomed) {
      return; // never send a non-hello app frame before the welcome (P2).
    }
    // [round-2 finding B] OMIT a null documentEpoch (mirrors the hello) so the
    // companion reads it as the document-less default, not an explicit epoch 0.
    this.sendFrame(buildDocumentChangedFrame({ documentId, documentEpoch }));
  }

  /**
   * ABB-S5 — send a browser STATE-CHANGE push frame (S5's `pushObservers` calls
   * this via the `PushSink.sendPush` DI). GATED fail-closed: only sent while
   * WELCOMED (never a non-hello app frame pre-welcome, mirroring
   * `sendDocumentChanged`) AND only from the CURRENT ACTIVE epoch (an
   * observer / demoted tab's push would be epoch-dropped / observer-dropped by the
   * companion anyway — `brokerReadFace.onBrowserPush`, SM-15 — but we drop it HERE
   * so a demoted tab emits NOTHING on the wire). The synthetic bridge actor +
   * `channel:'agent-bridge'` ride every push. pushObservers already gates on the
   * active epoch; this is the defence-in-depth wire gate.
   *
   * [round-4] BOTH the AUDIT and the SELECTION push stamp the CURRENT welcome epoch —
   * there is NO per-push epoch override. The audit push is correct under the current
   * epoch because S5 fires it SYNCHRONOUSLY at commit (the command.run handler invokes
   * `onTurnAuditCommitted` → `emitTurnAuditOnCommit` → here BEFORE its hash await), so at
   * the emit instant the current welcome epoch still equals the command's dispatch epoch
   * (no soft re-key has interleaved): the stamp is BOTH correctly segmented (the turn's
   * own epoch the rpc-result settles on via the minted-scope history) AND companion-
   * ACCEPTED. `brokerReadFace.onBrowserPush` DROPS any push whose `frame.sessionEpoch !==
   * currentQuad().sessionEpoch` (524-526); the round-3 captured-old-dispatch-epoch
   * OVERRIDE was exactly what that gate rejected after a re-key — it silently discarded
   * the committed audit bodies (never journaled / SSE'd). WS-FIFO ordering guarantees a
   * push leaves before any same-tab `document-changed` re-key frame, so the companion is
   * still on this epoch when it processes the push. Removing the override unifies the two
   * pushes onto the one correct epoch source (the current welcome epoch).
   */
  sendPush(event: OutboundPushFrame["event"], payload: unknown): void {
    if (!this.welcomed) {
      return; // never send a non-hello app frame before the welcome.
    }
    if (!this.session.isCurrentActiveEpoch()) {
      return; // an observer / demoted tab emits no push (SM-15).
    }
    // [round-4] Stamp the CURRENT welcome epoch for BOTH audit + selection. The audit
    // push's synchronous-at-commit emission (S5) guarantees this equals the command's
    // dispatch epoch — correctly segmented AND companion-accepted (no override needed).
    const epoch = this.session.getCurrentEpoch();
    if (epoch === null) {
      return; // defensive: active but no epoch (cannot happen) → no frame.
    }
    this.sendFrame(
      buildPushFrame({
        event,
        sessionEpoch: epoch,
        payload,
        actorEmail: BRIDGE_AUDIT_ACTOR_EMAIL,
      }),
    );
  }

  /**
   * ABB-S6 — send an `export-chunk` frame for an IN-FLIGHT export.dxf (S6's
   * `exportDxfHandler` producer calls this via the `sendChunk` DI). UNLIKE `sendPush`
   * (which stamps the CURRENT welcome epoch), an export chunk carries the export's ORIGIN
   * epoch (`frame.sessionEpoch`) — the companion's `ExportPump` matches a chunk by
   * `correlationId === exportId AND sessionEpoch === pending.sessionEpoch(=origin)`
   * (`exportBroker.ts`:242), so stamping the current (possibly soft-rekeyed) epoch would be
   * epoch-DROPPED. The frame is built by the producer with the origin epoch already; this
   * method GATES delivery: welcomed + open socket + the `(correlationId, sessionEpoch)` must
   * match a REGISTERED in-flight export's ORIGIN (the SAME registry `onExportControl` uses).
   * A chunk for an unknown/stale export is dropped (never streamed onto the wire after the
   * export's correlation left flight — defence-in-depth; the producer's epoch-halt already
   * stops it, but a late chunk from a racing producer must not leak).
   */
  sendExportChunk(frame: OutboundExportChunkFrame): void {
    if (!this.welcomed || this.socket === null) {
      return; // never a non-hello app frame pre-welcome / on a closed socket.
    }
    const originEpoch = this.inFlightExportOriginEpochs.get(frame.correlationId);
    if (originEpoch === undefined || frame.sessionEpoch !== originEpoch) {
      return; // not an in-flight export's chunk (or wrong epoch) → drop, never stream it.
    }
    this.rawSend(serializeFrame(buildExportChunkFrame(frame)));
  }

  // ── socket lifecycle ─────────────────────────────────────────────────────────

  private openSocket(): void {
    this.welcomed = false;
    // [P1] Bump the connection generation so an in-flight rpc handler dispatched on
    // a PRIOR connection cannot settle a command on THIS (new) socket.
    this.connectionGeneration += 1;
    this.setStatus(this.reconnectScheduler.attemptCount() > 0 ? "reconnecting" : "connecting");
    let socket: BridgeSocketLike;
    try {
      socket = this.socketFactory(this.wsUrl);
    } catch {
      // Factory threw (e.g. URL rejected) → treat as unreachable, fail-closed.
      this.setStatus("companion-not-reachable");
      return;
    }
    this.socket = socket;
    this.heartbeat = new HeartbeatResponder((text) => this.rawSend(text));
    socket.onopen = () => this.onOpen();
    socket.onclose = (event) => this.onClose(event.code, event.reason);
    socket.onerror = () => this.onError();
    socket.onmessage = (event) => this.onMessage(event.data);
  }

  private onOpen(): void {
    // Send the hello (token IN THE FRAME, never the URL). The welcome that
    // follows promotes us to connected; until then we are still connecting.
    const doc = this.deps.getHelloDocument();
    const info = this.deps.getClientInfo?.() ?? {};
    const hello = buildHelloFrame({
      token: this.token ?? "",
      clientInfo: info.clientInfo,
      tabId: info.tabId,
      documentId: doc.documentId,
      documentEpoch: doc.documentEpoch,
    });
    this.rawSend(serializeFrame(hello));
  }

  private onError(): void {
    // An onerror BEFORE the welcome = the companion is unreachable. (After a
    // welcome, an error precedes a close that drives the reconnect path.)
    if (!this.welcomed) {
      this.setStatus("companion-not-reachable");
    }
  }

  private onClose(code: number, reason?: string): void {
    const socket = this.socket;
    if (socket !== null) {
      this.detachHandlers(socket);
    }
    this.socket = null;
    this.heartbeat = null;
    // [round-4 P2 #2] Capture whether THIS connection ever reached `welcome` BEFORE the
    // session reset below clears state. `welcomed` is per-connection (reset to false in
    // `openSocket`, set true on a valid welcome in `onWelcome`), so a post-welcome
    // drop→reconnect→pre-welcome-fail sequence is classified by the LATEST connection's
    // own welcome state — not a stale flag from a prior connection.
    const wasWelcomed = this.welcomed;

    // ABORT any in-progress turn — the result is now undeliverable (the companion
    // already failed it 503), so we NEVER attempt to send it (SM-13).
    this.deps.onAbortInProgressTurn?.();
    this.session.reset();
    // [round-4 P2 #4] A drop ends every in-flight export (its controls + chunks are now
    // undeliverable / the companion already failed it 503) → clear the origin-epoch
    // registry so a post-reconnect stray control for a dead correlation is not routed.
    this.inFlightExportOriginEpochs.clear();
    // ABB-S6.4 (1A) HYDRATE-IN reset: a drop clears the document belief (a fresh hello →
    // new welcome re-hydrates it). A read between drop + re-welcome must not bind to a stale
    // belief — but the rpc epoch-drop already inerts a read before the new welcome.
    this.notifyDocumentBeliefReset();

    // [round-5 P2] DELEGATE the WHOLE reconnect decision to the single authority
    // `shouldAutoReconnect(code, deliberate, reason, welcomed)` — `onClose` no longer
    // pre-empts with its own pre-welcome 1008 branch (round-4's branch returned BEFORE
    // the round-2 retryable allow-list, so a `hello-deadline` 1008 — which is INHERENTLY
    // pre-welcome, `wsServer.ts`:620-623 — was wrongly stopped + re-prompted for a token
    // instead of opening a fresh socket; the allow-list and the welcomed-gating collided
    // on ORDERING). The decision encodes the COMPLETE matrix; here we ONLY:
    //   (a) act on its verdict (arm a reconnect, or stop), AND
    //   (b) SEPARATELY surface the right NON-reconnecting status when it returns false.
    const willReconnect = shouldAutoReconnect(code, this.deliberateClose, reason, wasWelcomed);

    if (!willReconnect) {
      if (this.deliberateClose) {
        return; // disconnect() already set `disconnected`; nothing further to surface.
      }
      // A non-reconnecting, non-deliberate close. Surface the status by CAUSE:
      //   • a 1008 (non-retryable: auth reject / malformed-hello family / reason-less)
      //     → token-rejected (re-prompt) + zero the dead token that re-fails identically;
      //   • a pre-welcome non-1008 (a failed INITIAL handshake — companion down / port
      //     refused) → companion-not-reachable (preserves the value `onError` already
      //     set for the error-then-1006 case — `setStatus` no-ops when already there).
      // A post-welcome non-1008 NEVER lands here (the matrix reconnects it); a retryable
      // reason NEVER lands here (the matrix reconnects it regardless of code/welcomed).
      if (code === WS_CLOSE_TOKEN_ROTATED) {
        this.token = null; // an auth/malformed-hello reject re-fails with the same inputs.
        this.setStatus("token-rejected");
      } else {
        this.setStatus("companion-not-reachable");
      }
      return;
    }

    // Auto-reconnect with backoff → a FRESH hello → new welcome → NEW epoch. A retryable
    // PRE-WELCOME reconnect (`hello-deadline`) re-sends the already-validated, trimmed
    // `this.token` on the next `openSocket`→`onOpen` hello (the token is preserved here —
    // only a non-retryable 1008 above zeroes it).
    this.setStatus("reconnecting");
    this.reconnectScheduler.schedule(() => {
      // [P2 double-open] Re-check at FIRE time: a re-entrant connect() between the
      // arm and the fire may already have opened a socket — opening another here
      // would orphan its listeners. Only open if still socket-less + connectable.
      if (this.socket === null && this.token !== null && !this.deliberateClose) {
        this.openSocket();
      }
    });
  }

  private detachHandlers(socket: BridgeSocketLike): void {
    socket.onopen = null;
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
  }

  // ── inbound frame dispatch ───────────────────────────────────────────────────

  private onMessage(data: unknown): void {
    const frame = parseInboundFrame(data);
    if (frame === null) {
      return; // malformed / unknown frame → dropped (fail-closed).
    }
    switch (frame.type) {
      case "welcome":
        this.onWelcome(frame);
        return;
      case "session-changed":
        this.onSessionChanged(frame);
        return;
      case "ping":
        // Answer the app-level ping synchronously from page JS (SM-14).
        this.heartbeat?.onPing(frame);
        return;
      case "rpc":
        // [round-3 P2 #4] `void` launches the async dispatch; harden it so a rejection
        // (a handler/serialization/builder throw that escaped the inner guards) can NEVER
        // leave the companion hanging — emit a minimal, safe fallback terminal instead.
        // `onRpc` itself sends a terminal on every NORMAL path, so this `.catch` only fires
        // on a residual escape (deeply defensive); it re-checks deliverability before the
        // fallback so it never sends onto a moved connection.
        void this.onRpc(frame).catch(() => {
          this.sendResidualFallbackTerminal(frame);
        });
        return;
      case "export-pause":
      case "export-resume":
      case "export-cancel":
        this.onExportControl(frame);
        return;
      case "takeover":
        // [round-3 P3 #5] v1 takes no action on a `takeover` control frame (the companion
        // legitimately emits `takeover{ok:false}`; the browser does not act on it — spec
        // §6 P2). `bye` is browser→companion ONLY (the companion RECEIVES it + closes
        // GOING_AWAY at `wsServer.ts`:864-866; it NEVER sends `bye`), so there is no
        // inbound `bye` arm — the codec drops a spurious inbound `bye` (fail-closed).
        return;
      default:
        return;
    }
  }

  private onWelcome(frame: InboundWelcomeFrame): void {
    // PROTOCOL-FAIL-CLOSED (INV-PROTO): a version mismatch refuses the connected
    // state — accept/send NO rpc. We hold the socket open only to surface status;
    // the client never promotes to connected and never adopts the quad.
    if (frame.protocolVersion !== AGENT_BRIDGE_PROTOCOL_VERSION) {
      this.welcomed = false;
      this.setStatus("protocol-version-mismatch");
      // Close the unusable socket deliberately (no auto-reconnect into the same
      // mismatch). The connect surface (S7) re-prompts only on a new build.
      this.deliberateClose = true;
      const s = this.socket;
      if (s !== null) {
        this.socket = null;
        this.detachHandlers(s);
        try {
          // [round-4 P2 #1] Use the browser-ALLOWED app code 4002, NOT the reserved
          // `1002` "protocol error": `WebSocket.close(1002)` THROWS synchronously
          // (InvalidAccessError), and the handlers are already detached + `this.socket`
          // nulled above — so the throw would be swallowed AFTER teardown and the socket
          // would NEVER actually close, lingering on the companion until heartbeat lease-
          // expiry while holding the active seat (forwarded commands then HANG). A 4xxx
          // app code closes cleanly + carries the distinct protocol-mismatch meaning.
          s.close(WS_CLOSE_PROTOCOL_MISMATCH, "protocol-version-mismatch");
        } catch {
          /* harmless — defensive only; 4002 is browser-allowed so this should not throw */
        }
      }
      // [round-3 P3 #6] Null the heartbeat responder for teardown-symmetry with
      // disconnect() + onClose() (every other socket teardown frees it). The responder
      // owns no real resource, but a dangling reference to a dead socket's sink is a
      // cleanup leak the other paths do not have.
      this.heartbeat = null;
      // [sibling P2] ABORT any in-progress turn — teardown-symmetry with disconnect() +
      // onClose() (both abort). A protocol-mismatch refuses the connected state on an
      // UNUSABLE socket; an in-flight turn dispatched on the pre-mismatch connection can
      // never deliver its result, so abort it now (idempotent — no-ops if no turn is in
      // flight) rather than leaking S4's in-flight latch + S5's staged audit into the next
      // session. The handlers are already detached above, so onClose cannot also run for
      // this teardown — exactly one abort.
      this.deps.onAbortInProgressTurn?.();
      this.session.reset();
      // [sibling P3] Symmetry with disconnect() + onClose(): an unusable-socket teardown also
      // ends every in-flight export → clear the origin-epoch registry (no dead-correlation /
      // epoch leak into the next session; the socket is closed, its export controls are moot).
      this.inFlightExportOriginEpochs.clear();
      // ABB-S6.4 (1A) HYDRATE-IN reset: a protocol-mismatch refuses the connected state →
      // clear the document belief (no read is served on an unusable socket anyway).
      this.notifyDocumentBeliefReset();
      return;
    }

    // Valid welcome → adopt the authoritative ownership quad/role/epoch. Accepted
    // at ANY time post-pairing (an unsolicited welcome{epoch+1} after our
    // document-changed re-keys us — SM-12). Idempotent for the same epoch.
    this.welcomed = true;
    // [round-3 P2 #1] `adoptWelcome` returns `false` on a STALE lower-epoch welcome
    // (it mutates nothing then). The status / role / doc-refresh side-effects below
    // MUST gate on this + read the TRACKER authority, NOT the raw stale frame: a
    // demoted observer replayed a stale welcome{active,N} would otherwise flip to
    // `connected-active` off `frame.role` while the tracker still says observer
    // (isCurrentActiveEpoch() false) AND fire a spurious `document-changed`.
    const adopted = this.session.adoptWelcome(frame);
    // [P2 double-open] CANCEL (not just reset): a welcome can arrive while a stray
    // reconnect timer is still armed (e.g. a fast reconnect that welcomed before the
    // prior backoff timer fired). reset() zeroes the backoff but leaves the armed
    // timer live — it would later open a second socket. cancel() clears it; reset()
    // then returns the backoff to base for the next genuine drop. (Always safe to run,
    // even on a stale welcome — the socket is genuinely open.)
    this.reconnectScheduler.cancel();
    this.reconnectScheduler.reset();
    if (!adopted) {
      // [round-3 P2 #1] A stale lower-epoch welcome: the tracker ignored it, so we run
      // NO status / role / doc side-effects (they would diverge from the authority).
      // Re-sync the status to the tracker's authority defensively (it is unchanged by
      // a stale welcome, so this is a no-op when already consistent).
      this.syncConnectedStatusToRole();
      return;
    }
    // [round-3 P2 #1] Derive the connected status from the TRACKER authority (role +
    // demotion), NOT the raw `frame.role` — they can disagree if a higher broadcast
    // epoch already demoted us. The tracker just adopted this welcome, so its role/epoch
    // ARE the authority.
    this.setStatus(
      this.session.isCurrentActiveEpoch() ? "connected-active" : "connected-observer",
    );
    // [round-2 finding B] OBSERVER document-identity refresh on promotion. While an
    // OBSERVER, this tab's `document-changed` SENDS are ignored by the companion
    // (`onDocumentChanged` returns early off the active seat — `wsServer.ts`:960), so
    // the companion's record keeps this tab's ORIGINAL hello document. On promotion
    // (`promoteFirstObserver`/`takeover`) the companion mints the active session from
    // that STALE record document (`session.ts`:323/432 → `mintSeat`), so the welcome
    // carries the stale identity and a later command / expectedSession would target the
    // WRONG document. The fix: on EVERY welcome that makes me ACTIVE, if my LIVE local
    // document differs from the document the companion just told me it has (the welcome
    // quad = the companion's belief), send a `document-changed` NOW — accepted because
    // I am the active seat → the companion soft-re-keys to the correct document BEFORE
    // any command targets it. No-op when they already agree (a normal active welcome,
    // or my own prior soft-re-key already landed) so it is not a spurious wire frame.
    //
    // [round-3 P2 #1] Gate on the TRACKER's authoritative role (`getRole() === "active"`),
    // NOT `frame.role`, and compare against the tracker's authoritative quad — so a
    // welcome that the authority does NOT treat as the current active epoch never sends a
    // spurious refresh.
    if (this.session.getRole() === "active") {
      this.refreshDocumentIdentityIfStale();
    }
    // ABB-S6.4 (1A) HYDRATE-IN: surface the adopted document/session belief so the read seam
    // binds to the correct snapshot (the SYMMETRIC half of the project-OUT refresh above).
    this.notifyDocumentBelief();
  }

  /**
   * [round-2 finding B] If the LIVE local document identity differs from what the
   * companion believes (the just-adopted welcome quad), send a `document-changed` so
   * the companion soft-re-keys to the correct document. Called only on a welcome that
   * makes this tab ACTIVE (an observer's `document-changed` would be ignored). The
   * comparison is against the companion's authoritative belief, so a stale-on-promotion
   * identity is corrected exactly once and an already-consistent identity sends nothing.
   *
   * [round-3 P2 #1] Compares against the TRACKER's authoritative quad
   * (`session.getQuad()`), NOT a raw welcome frame — the quad is the value the tracker
   * actually ADOPTED (a stale lower-epoch welcome never reaches here, gated by the
   * `adopted` check + role authority in `onWelcome`), so the comparison can never be
   * skewed by an ignored frame.
   *
   * Gate #4 P3 — documentEpoch HYDRATE-HALF (DEFERRED to ABB-S6, latent now):
   * this is the project-OUT half (local swap → `document-changed` to the
   * companion). The SYMMETRIC hydrate-IN half — surfacing a NEW companion-side
   * documentEpoch (a remote/late swap reflected in a welcome / session-changed)
   * UP to the agent's read consumer so its cached document belief refreshes — is
   * NOT wired here. It is INERT until ABB-S6 builds the `state.read` / hydrate
   * path (there is no read consumer to notify before S6). S6 MUST wire the
   * hydrate half (notify the read seam on a documentEpoch change) to complete the
   * project-out ⇔ hydrate-in symmetry; tracked as the S6 gate-marker.
   */
  private refreshDocumentIdentityIfStale(): void {
    const local = this.deps.getHelloDocument();
    const quad = this.session.getQuad();
    if (quad === null) {
      return; // defensive: not paired (the authority has no document belief yet).
    }
    if (local.documentId === quad.documentId && local.documentEpoch === quad.documentEpoch) {
      return; // the companion already has my current document — no spurious frame.
    }
    // The companion's record is stale (it never saw my observer-era swap). Re-assert my
    // CURRENT document now that I am active; the companion accepts it + soft-re-keys.
    // `sendDocumentChanged` guards on `welcomed` (true here) and a closed socket, and
    // omits a null epoch faithfully (no coercion to 0).
    this.sendDocumentChanged(local.documentId, local.documentEpoch);
  }

  private onSessionChanged(frame: InboundSessionChangedFrame): void {
    // Demotion detection: a HIGHER broadcast epoch supersedes me
    // (isCurrentActiveEpoch() flips false → S5 suppresses pushes, S4 self-aborts);
    // the SAME epoch is idempotent (the auto-promotion welcome+session-changed
    // pair). The tracker owns the rule; it re-surfaces role to S5.
    this.session.applySessionChanged(frame);
    // [round-2 finding D] The client STATUS must agree with the role/epoch authority.
    // Round-1 demoted `role`→observer on a superseding session-changed but left the
    // STATUS at `connected-active` (only `onWelcome` set status), so `getStatus()` /
    // `onStatusChange` LIED that the tab was active while `getRole()` /
    // `isCurrentActiveEpoch()` said observer. Re-sync the status to the tracker's
    // authority: demoted → `connected-observer`; still-active → `connected-active`.
    // Only while paired (a welcome epoch exists) — a session-changed before any
    // welcome does not invent a connected status. (A re-promotion arrives via a fresh
    // `welcome`, which sets the active status in `onWelcome`.)
    this.syncConnectedStatusToRole();
    // ABB-S6.4 (1A) HYDRATE-IN: a session-changed adopts a new epoch (a re-key / demotion);
    // surface the (possibly unchanged) belief so the read seam stays bound to the live
    // snapshot. The cache no-ops an unchanged belief; a real swap re-keyed the document.
    this.notifyDocumentBelief();
  }

  /**
   * [round-2 finding D] Re-derive the connected status from the session tracker's
   * role/epoch authority so STATUS, `getRole()`, and `isCurrentActiveEpoch()` never
   * disagree. Only adjusts BETWEEN the two connected statuses while paired; it never
   * overrides a non-connected status (disconnected / reconnecting / token-rejected /
   * mismatch are owned by their own transitions).
   */
  private syncConnectedStatusToRole(): void {
    if (this.status !== "connected-active" && this.status !== "connected-observer") {
      return; // not in a connected status → its owning transition governs it.
    }
    if (this.session.getCurrentEpoch() === null) {
      return; // not paired (defensive) → leave the status to its owner.
    }
    this.setStatus(
      this.session.isCurrentActiveEpoch() ? "connected-active" : "connected-observer",
    );
  }

  /**
   * ABB-S6.4 (1A) HYDRATE-IN: surface the companion's CURRENT document/session belief to
   * the DI `onDocumentChanged` (S7 → `documentEpochCache.hydrate`). Reads the TRACKER's
   * authoritative quad (the value just adopted), so a `state.read` binds to the correct
   * snapshot belief. Called on EVERY adoption (welcome / session-changed); the cache no-ops
   * an unchanged belief. This is the SYMMETRIC half of the project-OUT `document-changed`
   * send (`refreshDocumentIdentityIfStale`) — together they close the project-OUT ⇔
   * hydrate-IN symmetry (the S6 gate-marker).
   */
  private notifyDocumentBelief(): void {
    const quad = this.session.getQuad();
    this.deps.onDocumentChanged?.({
      documentId: quad?.documentId ?? null,
      documentEpoch: quad?.documentEpoch ?? null,
      sessionEpoch: quad?.sessionEpoch ?? null,
    });
  }

  /**
   * ABB-S6.4 (1A) HYDRATE-IN reset: clear the cached belief on a teardown (disconnect /
   * drop / protocol-mismatch). S7 wires it to `documentEpochCache.reset()` so a read after a
   * hard session death never binds to a stale pre-death document belief.
   */
  private notifyDocumentBeliefReset(): void {
    this.deps.onDocumentChanged?.({
      documentId: null,
      documentEpoch: null,
      sessionEpoch: null,
    });
  }

  private async onRpc(frame: InboundRpcFrame): Promise<void> {
    // EPOCH-DROP: an rpc whose sessionEpoch ≠ our current welcome epoch is dropped
    // (the browser ownership guard — a stale/old-epoch command is inert, SM-12).
    const epoch = this.session.getCurrentEpoch();
    if (epoch === null || frame.sessionEpoch !== epoch) {
      return;
    }

    const handler = this.deps.handlers?.[frame.method];

    // A known-but-unwired method OR a genuinely unknown method → a documented
    // not-implemented reject (NEVER a silent drop — that hangs the companion
    // deadline, SM-10). Both map to `browser-dispatch-error` per the spec.
    if (handler === undefined) {
      const reason = isKnownRpcMethod(frame.method)
        ? `rpc method "${frame.method}" is not yet wired in this build`
        : `unknown rpc method "${frame.method}"`;
      // [round-2 finding C] Route by method: an unwired/unknown `export.dxf` fails on
      // the EXPORT wire (`export-error`, matched by the companion's `ExportPump`), every
      // other method on the generic `rpc-error` path. `serializeFailureTerminal` owns the
      // routing so the unwired, `{ok:false}`, and throw sites all stay consistent.
      this.rawSend(serializeFailureTerminal(frame, {
        code: "browser-dispatch-error",
        message: reason,
      }));
      return;
    }

    // [P1] CAPTURE the dispatch-time connection identity: the socket REFERENCE
    // (object identity), the welcome `bootId`, AND the connection generation. A late
    // result is bound to THIS exact connection before it is sent (see the guard
    // below) so a stale handler that completes after a reconnect to a RESTARTED
    // companion — which can REUSE the same sessionEpoch under a NEW bootId — can
    // never settle the WRONG command on the new socket.
    const dispatchSocket = this.socket;
    const dispatchBootId = this.session.getQuad()?.bootId ?? null;
    const dispatchGeneration = this.connectionGeneration;

    // [round-4 P2 #4] Register a WIRED export.dxf as IN-FLIGHT keyed by correlationId →
    // its ORIGIN epoch (= the dispatch epoch, which equals the current epoch here as it
    // just passed the epoch-drop gate). While the producer runs, a soft re-key may advance
    // the current epoch, but the companion still stamps this export's flow-control frames
    // with the ORIGIN epoch — so `onExportControl` routes them by this registry, NOT the
    // (now-advanced) current welcome epoch. Cleared in `finally` once the terminal resolves
    // (or on a drop via `session reset`). Only `export.dxf` registers (other methods never
    // receive export-control frames). `frame.sessionEpoch === epoch` was just asserted.
    const isExport = frame.method === "export.dxf";
    if (isExport) {
      this.inFlightExportOriginEpochs.set(frame.correlationId, frame.sessionEpoch);
    }

    let outcome: RpcHandlerOutcome;
    try {
      outcome = await handler({
        correlationId: frame.correlationId,
        sessionEpoch: frame.sessionEpoch,
        deadlineBudgetMs: frame.deadlineBudgetMs,
        payload: frame.payload,
      });
    } catch (err) {
      // [round-4 P2 #4] The export terminated (threw) → it is no longer in flight; drop its
      // registry entry so a LATE control frame for this dead correlation is not routed.
      if (isExport) {
        this.inFlightExportOriginEpochs.delete(frame.correlationId);
      }
      // A handler that THREW (rather than returning {ok:false}) → the generic
      // dispatch-error terminal (NEVER a silent drop) — but ONLY if the reply still
      // binds to the dispatch connection (P1); otherwise drop it.
      if (!this.canDeliverReply(frame, dispatchSocket, dispatchBootId, dispatchGeneration)) {
        return;
      }
      // [round-2 finding C] A thrown export.dxf handler ALSO fails on the export wire.
      this.rawSend(serializeFailureTerminal(frame, {
        code: "browser-dispatch-error",
        message: err instanceof Error ? err.message : "browser dispatch threw",
      }));
      return;
    }

    // [round-4 P2 #4] The export terminated (resolved) → it is no longer in flight; drop its
    // registry entry (BEFORE the deliverability check so it is cleared even on a moved
    // connection — the producer is done, its controls are moot).
    if (isExport) {
      this.inFlightExportOriginEpochs.delete(frame.correlationId);
    }

    // [P1] The socket may have dropped / reconnected while the handler ran (async).
    // Bind the reply to the FULL dispatch identity (socket reference + bootId +
    // generation + epoch), not just "some open socket + numeric epoch" — a restarted
    // companion can reuse the epoch under a new bootId. If the connection moved, the
    // result is undeliverable (the companion failed it 503) — do NOT send it.
    if (!this.canDeliverReply(frame, dispatchSocket, dispatchBootId, dispatchGeneration)) {
      return;
    }

    if (outcome.ok) {
      // [round-3 P2 #3] A SUCCESSFUL terminal is ALSO routed by method: an `export.dxf`
      // success replies `export-complete` on the EXPORT wire (the companion's pump never
      // matches an `rpc-result` carrying an export correlationId → drop → HTTP hang),
      // every other method replies `rpc-result`. `serializeSuccessTerminal` owns the routing
      // so the success / failure / unwired / throw paths for export.dxf ALL resolve to
      // export-* frames (no generic rpc terminal can leak an export correlationId).
      this.rawSend(serializeSuccessTerminal(frame, outcome satisfies SuccessOutcome));
    } else {
      // [round-2 finding C] A wired handler's `{ok:false}` for export.dxf MUST fail on
      // the export wire too (round-1 only special-cased the UNWIRED case) — else the
      // wired S6 producer's validation/dispatch failure mis-routes to rpc-error and the
      // HTTP export hangs. `serializeFailureTerminal` routes by method.
      this.rawSend(serializeFailureTerminal(frame, outcome satisfies FailureOutcome));
    }
  }

  /**
   * [P1, round-2 finding A] Whether a late rpc reply may be delivered. The guard keys
   * on the CONNECTION IDENTITY, NOT the epoch: deliverable IFF the CURRENT socket is
   * the EXACT socket the handler was dispatched on (object identity) AND the connection
   * GENERATION is unchanged (no reconnect happened) AND the welcome `bootId` still
   * matches the dispatch boot. The numeric `sessionEpoch` is DELIBERATELY NOT required
   * to equal the current epoch:
   *
   *   • A RESTARTED companion (the case round-1 caught) = a NEW socket + a NEW bootId
   *     (and a bumped generation), so the socket/boot/generation triad already drops
   *     the stale reply — even when the restarted companion REUSES the same numeric
   *     epoch. The epoch check was redundant for that case.
   *   • A SAME-socket SOFT-REKEY (the case round-1 BROKE): while a handler runs, the
   *     companion sends `document-changed`→`welcome{epoch+1}` on the SAME socket, so
   *     `getCurrentEpoch()` advances past the dispatch epoch. The reply is sent with
   *     the DISPATCH-time epoch (`frame.sessionEpoch`, unchanged), which the companion's
   *     `onRpcReply` matches against the SOCKET's minted-scope HISTORY (`wsServer.ts`
   *     :1074 `mintedScopes.find(s => s.sessionEpoch === claimedEpoch)`) — the OLD
   *     scope is retained while it has an outstanding reserved/in-flight entry
   *     (`wsServer.ts` captureMintedScope, F2). Requiring `currentEpoch === dispatchEpoch`
   *     would WRONGLY DROP this legitimate reply → the HTTP command waits out its
   *     deadline + loses the finished outcome.
   *
   * The reply ECHOES the DISPATCH epoch (`frame.sessionEpoch`), which is what the
   * companion expects for its history match — NOT the current (advanced) epoch.
   */
  private canDeliverReply(
    _frame: InboundRpcFrame,
    dispatchSocket: BridgeSocketLike | null,
    dispatchBootId: string | null,
    dispatchGeneration: number,
  ): boolean {
    return (
      this.socket !== null &&
      this.socket === dispatchSocket &&
      this.connectionGeneration === dispatchGeneration &&
      this.session.getQuad()?.bootId === dispatchBootId
    );
  }

  /**
   * Route a parsed export FLOW-CONTROL frame (`export-pause`/`-resume`/`-cancel`) to
   * the DI export-control seam, keyed by the in-flight export's ORIGIN epoch — NOT the
   * current welcome epoch.
   *
   * [round-4 P2 #4] The companion stamps every export-control frame with the export's
   * ORIGIN epoch (`exportBroker.ts` `PendingExport.sessionEpoch`, captured at
   * `beginExport`), and on a soft re-key it ABORTS the pending export + sends
   * `export-cancel{sessionEpoch:ORIGIN}` to the ORIGINATING socket AFTER the socket has
   * already advanced to a NEW current welcome epoch. The old "frame.sessionEpoch === current
   * epoch → else drop" gate therefore WRONGLY dropped the cancel for a superseded export,
   * leaving the wired S6 producer streaming useless old-epoch chunks past the aborted HTTP
   * response. So route a control frame when EITHER:
   *   • its `(correlationId, sessionEpoch)` matches an IN-FLIGHT export's ORIGIN (the
   *     soft-rekey / origin-epoch case — accept the control even though the current epoch
   *     advanced); OR
   *   • its `sessionEpoch` equals the CURRENT welcome epoch (the normal no-soft-rekey case,
   *     plus a wired producer that has not registered an in-flight entry on this client —
   *     preserves the §5.0 same-epoch routing).
   * Everything else is epoch-DROPPED (a stale-epoch control for an UNKNOWN correlation is
   * inert). The PRE-WELCOME drop is kept (`getCurrentEpoch() === null` → no in-flight export
   * possible — the rpc/onExportControl pre-welcome sibling). Inert until ABB-S6 wires
   * `onExportControl` — but NEVER silently dropped when it matches (a dropped pause/cancel
   * would let the wired producer stream past the companion's credit cap, P2).
   */
  private onExportControl(frame: InboundExportControlFrame): void {
    const epoch = this.session.getCurrentEpoch();
    if (epoch === null) {
      return; // pre-welcome: no in-flight export possible → inert (rpc-drop sibling).
    }
    const originEpoch = this.inFlightExportOriginEpochs.get(frame.correlationId);
    const matchesInFlightOrigin =
      originEpoch !== undefined && frame.sessionEpoch === originEpoch;
    const matchesCurrentEpoch = frame.sessionEpoch === epoch;
    if (!matchesInFlightOrigin && !matchesCurrentEpoch) {
      return; // epoch-drop: a stale-epoch control for an unknown correlation is inert.
    }
    this.deps.onExportControl?.(frame);
  }

  // ── outbound helpers ─────────────────────────────────────────────────────────
  //
  // [round-4 split] The terminal-frame ROUTING + serialization (success/failure by
  // method → export-* vs rpc-* wire, the byteLength accounting read, the
  // non-serializable fallback) lives in the PURE sibling `./outboundTerminals` (the
  // client crossed the 1000-line soft-limit after the 4×P2 fixes). `onRpc` calls
  // `serializeSuccessTerminal` / `serializeFailureTerminal` directly + pipes the text
  // through `rawSend`. The client keeps ONLY the socket-stateful wrappers below.

  /**
   * [round-3 P2 #4] The LAST-RESORT terminal for a residual `onRpc` rejection that
   * escaped every inner guard (`onMessage`'s `.catch`). Sends the minimal safe fallback
   * (keyed by method) so the companion is never left waiting out its deadline — gated on
   * an OPEN, welcomed socket so it never writes onto a moved/closed connection (a reply
   * after a drop/reconnect is undeliverable anyway). `rawSend` no-ops on a null socket;
   * `serializeFallbackTerminal` uses only safe primitives → it cannot itself throw.
   */
  private sendResidualFallbackTerminal(frame: InboundRpcFrame): void {
    if (this.socket === null || !this.welcomed) {
      return; // the connection moved / closed → the reply is undeliverable, drop it.
    }
    this.rawSend(serializeFallbackTerminal(frame));
  }

  /** Serialize + send a CONTROL / push frame on the open socket. */
  private sendFrame(
    frame: OutboundDocumentChangedFrame | OutboundPushFrame,
  ): void {
    this.rawSend(serializeFrame(frame));
  }

  /** Raw text send guarded on an open socket (heartbeat + frames share this). */
  private rawSend(text: string): void {
    if (this.socket === null) {
      return;
    }
    try {
      this.socket.send(text);
    } catch {
      /* a send on a closing socket is harmless — the close handler runs next */
    }
  }

  // ── status ───────────────────────────────────────────────────────────────────

  private setStatus(status: AgentBridgeStatus): void {
    if (this.status === status) {
      return;
    }
    this.status = status;
    this.deps.onStatusChange?.(status);
  }
}
